import { compare } from 'compare-versions';
import { all, call, put, takeLatest, select } from 'redux-saga/effects';

import { PROJECTION } from 'constants/ApiConstants';
import { SORT } from 'constants/FilterConstants';
import { getLocalUserPreferences, USER_PREFERENCES } from 'hooks/useLocalUserPreferences';
import { putAndWait } from 'modules/app/sagas';
import { getCurrentUserId } from 'modules/users/selectors/currentUser';

import packageJson from '../../../../package.json';
import { APPLICATION_LOADED } from '../../app/actionTypes';
import { fetchReleaseById, fetchReleases } from '../actions/core';
import { openReleaseModal } from '../actions/release-modal';
import { OPEN_RELEASE_MODAL, UPDATE_RELEASE_MODAL } from '../actionTypes';
import { MODAL_SOURCE, RELEASE_STATE, RELEASE_TYPE } from '../constants';
import { filterDisplayableReleases } from '../utils/release-modal';

const { version: CURRENT_VERSION } = packageJson;

export const RELEASE_MODAL_FETCH_PARAMS = {
  size: 10,
  projection: PROJECTION.PAGE,
  releaseVersionTo: CURRENT_VERSION,
  releaseStates: [RELEASE_STATE.PUBLISHED],
  releasesTypes: [RELEASE_TYPE.WEB],
  [SORT]: 'releaseDate,DESC',
};

function* getLatestRelease() {
  const { payload, error } = yield putAndWait(fetchReleases(RELEASE_MODAL_FETCH_PARAMS, { source: MODAL_SOURCE }));
  return !error ? filterDisplayableReleases(payload?.content ?? []).at(0) : null;
}

function* getLastDisplayedReleaseNote() {
  const userId = yield select(getCurrentUserId);
  const userPreferences = getLocalUserPreferences(userId);
  return userPreferences?.[USER_PREFERENCES.LAST_RELEASE_NOTE];
}

function* startApplicationSideEffect() {
  const [latestRelease, lastDisplayed] = yield all([call(getLatestRelease), call(getLastDisplayedReleaseNote)]);

  if (latestRelease) {
    const { payload: detailedReleasePayload, error: detailedReleaseError } = yield putAndWait(fetchReleaseById(latestRelease.id, { source: MODAL_SOURCE }));
    const shouldOpen = !lastDisplayed || (
      (lastDisplayed.id !== latestRelease.id || lastDisplayed.etag !== detailedReleasePayload?.etag) &&
      (!lastDisplayed.releaseVersion || compare(latestRelease.releaseVersion, lastDisplayed.releaseVersion, '>='))
    );
    if (!detailedReleaseError && shouldOpen) {
      yield put(openReleaseModal(latestRelease.id, false, false));
    }
  }
}

function* updateReleaseModalSideEffect({ payload: { releaseId, fetchDetailedRelease } }) {
  if (fetchDetailedRelease) {
    yield put(fetchReleaseById(releaseId, { source: MODAL_SOURCE }));
  }
}

export default function* ReleaseModalSaga() {
  yield takeLatest(APPLICATION_LOADED, startApplicationSideEffect);
  yield takeLatest([OPEN_RELEASE_MODAL, UPDATE_RELEASE_MODAL], updateReleaseModalSideEffect);
}
